import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api/apiClient";
import { AccountShell } from "./accountLink";

/**
 * G-136 — "Forgot password?" from the sign-in page. English, like the sign-in
 * page itself: nobody is known here, so there is no one's language to speak.
 *
 * The answer is the same whatever was typed — the server does not say
 * whether an account exists, and neither does this page. A link goes only to
 * an address its owner has CONFIRMED; staff who never confirmed theirs are
 * told to ask whoever manages them, who can send a reset from Team.
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    const trimmed = email.trim();
    if (!trimmed.includes("@")) {
      setError("Type the email address you sign in with.");
      return;
    }
    setBusy(true);
    try {
      await api.requestPasswordReset(trimmed);
      setSent(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not send the request. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <AccountShell>
        <h1 className="page-title account-center">Check your inbox</h1>
        <p className="account-center">
          If <strong>{email.trim()}</strong> belongs to an account with a confirmed address, a link
          to choose a new password is on its way. It works once, for 30 minutes.
        </p>
        <p className="muted account-center">
          Nothing arrived? Your address may not be confirmed yet — ask your manager or the shop
          owner to send you a reset link from the Team page.
        </p>
        <Link className="button" to="/login">
          Back to sign in
        </Link>
      </AccountShell>
    );
  }

  return (
    <AccountShell>
      <h1 className="page-title account-center">Forgot your password?</h1>
      <p className="account-center">
        Type the address you sign in with and we will email you a link to choose a new one.
      </p>
      <form className="account-password-form" onSubmit={submit} noValidate>
        {error ? <div className="alert error">{error}</div> : null}
        <div className="field">
          <label htmlFor="forgot-email">Email</label>
          <input
            id="forgot-email"
            type="email"
            autoComplete="username"
            maxLength={254}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="button-row">
          <Link className="button plain" to="/login">
            Back to sign in
          </Link>
          <button className="button" type="submit" disabled={busy}>
            {busy ? "Sending…" : "Send link"}
          </button>
        </div>
      </form>
    </AccountShell>
  );
}
